import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import IconSizeEnum from '@models/enums/IconSizeEnum';
import { IImage } from '@models/IImage';
import { IMeta } from '@models/IMeta';
import useIsTouchDevice from '@utils/hooks/useIsTouchDevice';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

interface Props {
  readonly title: string;
  readonly text: string;
  readonly fpsImages: IImage[];
  readonly productImage: IImage;
  readonly shutterX: number;
  readonly shutterY: number;
  readonly shutterReleaseButtonSize: 'small' | 'medium' | 'large';
  readonly shutterReleaseCaption: string;
  readonly meta: IMeta;
}

const FPSShowcase = ({
  title,
  text,
  fpsImages,
  productImage,
  shutterX = 50,
  shutterY = 50,
  shutterReleaseButtonSize = 'medium',
  shutterReleaseCaption,
  meta,
}: Props) => {
  const intervalRef = useRef<ReturnType<typeof setInterval>>();
  const isTouchDevice = useIsTouchDevice();
  const [startCounter, setStartCounter] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (currentImageIndex === fpsImages.length) {
    setCurrentImageIndex(0);
  }

  useEffect(() => {
    if (startCounter) {
      intervalRef.current = setInterval(() => {
        setCurrentImageIndex((counter) => counter + 1);
      }, 1000 / fpsImages.length);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [startCounter, fpsImages.length]);

  const releaseButtonSizes = {
    small: 'h-[10px] w-[10px] lg:h-[15px] lg:w-[15px]',
    medium: 'h-[15px] w-[15px] lg:h-[20px] lg:w-[20px]',
    large: 'h-[25px] w-[25px] lg:h-[30px] lg:w-[30px]',
  };

  const disabledKeys = ['ArrowUp', 'ArrowDown', 'Meta', 'Control', 'r'];

  return (
    <section className="fps-shutter component-padding bg-black-100 text-white">
      <div className="container grid grid-cols-12">
        <div className="order-3 col-span-12 col-start-1 pt-15 lg:order-1 lg:col-span-6 lg:pb-15 lg:pt-0 xl:col-span-3 xl:col-start-2 xl:pt-[200px]">
          {title && (
            <h2 className="h3" data-epi-property-name={meta?.editHints?.title}>
              {title}
            </h2>
          )}

          {text && (
            <p
              className="body-two mt-7.5 text-grey-500"
              data-epi-property-name={meta?.editHints?.text}
            >
              {text}
            </p>
          )}
        </div>

        <div className="h-33 relative order-1 col-span-12 col-start-1 lg:order-2 xl:col-span-7 xl:col-start-6">
          {fpsImages.map((image, index) => (
            <Image
              image={image}
              key={image?.desktop?.url}
              imageClasses={classNames(
                { 'invisible block absolute': currentImageIndex !== index },
                'w-full h-full object-contain'
              )}
              epiEdit={meta?.editHints?.fPSImages as string | undefined}
              dataTestId={`CT-61-Active-Image-${currentImageIndex}`}
            />
          ))}
        </div>

        {productImage && (
          <div
            onContextMenu={(ev) => {
              if (isTouchDevice) ev.preventDefault();
            }}
            className="noselect relative order-2 col-span-6 -mt-20 md:-mt-44 lg:order-3 lg:col-span-5 lg:col-start-1 xl:col-span-3 xl:col-start-6 xl:-mt-[200px]"
          >
            <Image
              image={productImage}
              imageClasses="w-full h-full object-contain"
              epiEdit={meta?.editHints?.productImage as string | undefined}
            />

            <div
              className="absolute -translate-x-1/2 translate-y-1/2"
              style={{ left: `${shutterX}%`, bottom: `${shutterY}%` }}
            >
              <button
                type="button"
                onKeyDown={(event) => {
                  if (disabledKeys.includes(event.key)) return;
                  setStartCounter(true);
                }}
                onKeyUp={() => {
                  setStartCounter(false);
                  setCurrentImageIndex(0);
                }}
                onMouseDown={() => {
                  setStartCounter(true);
                }}
                onMouseUp={() => {
                  setStartCounter(false);
                  setCurrentImageIndex(0);
                }}
                onTouchStart={() => {
                  setStartCounter(true);
                }}
                onTouchEnd={() => {
                  setStartCounter(false);
                  setCurrentImageIndex(0);
                }}
                className={classNames(
                  'flex content-center items-center justify-center rounded-full bg-yellow hover:bg-yellow-100 focus:bg-yellow-500',
                  releaseButtonSizes[shutterReleaseButtonSize]
                )}
              >
                <p className="sr-only">Play</p>
                <motion.div className="pointer-events-none absolute h-10 w-10 -translate-x-1/2 lg:h-20 lg:w-20">
                  {Array.from(Array(4).keys()).map((value) => (
                    <motion.span
                      key={value}
                      className="absolute h-full w-full rounded-full border-2 border-white"
                      initial={{ opacity: 1, scale: 0 }}
                      animate={{ opacity: 0, scale: 1.25 }}
                      transition={{
                        ease: 'easeOut',
                        duration: 4,
                        delay: value * 1,
                        repeat: Infinity,
                        repeatDelay: 0.01,
                      }}
                    />
                  ))}
                </motion.div>

                <div className="relative h-full w-full focus:outline-none">
                  <p
                    className="h6 pointer-events-none absolute left-9 top-1/2 flex w-[170px] -translate-y-[15px] text-left focus:outline-none lg:left-16 lg:w-[280px]"
                    data-epi-property-name={
                      meta?.editHints?.shutterReleaseCaption as
                        | string
                        | undefined
                    }
                  >
                    <span className="lg:pt-1 ">
                      <Icon
                        name="arrow_left"
                        colorClassName="text-yellow"
                        size={IconSizeEnum.xl}
                      />
                    </span>
                    {shutterReleaseCaption}
                  </p>
                </div>
              </button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default FPSShowcase;
